import { ComponentType } from '@angular/cdk/overlay';
import { IconsetKey } from '../../../whitelabel/iconset-key.enum';
import { ThemeKey } from '../../../whitelabel/theme-key.enum';
import { WhitelabeledComponentKey } from '../../../whitelabel/whitelabel-component-types';
import { MaintenanceColumnKeys } from '../../pages/maintenance/maintenance-task/enums/maintenance-column-keys.enum';
import { BaseDataControl } from '../../pages/resources/equipment/components/equipments-details/components/general-data/base-data/enums/base-data-control.enum';
import { EquipmentColumnKeys } from '../../pages/resources/equipment/enums/equipment-column-keys.enum';
import { AutomatedTransferColumnKeys } from '../../pages/transfers/automated-transfers/enums/automated-transfer-column-keys.enum';

export type WhitelabelComponentMap = Partial<{
  [key in WhitelabeledComponentKey]?: () => Promise<ComponentType<unknown>>;
}>;

/**
 * Defines the contract for a whitelabel definition.
 */
export type Whitelabel = {
  whitelabeledComponents: WhitelabelComponentMap;
  iconSet: IconsetKey;
  defaultTheme: ThemeKey;
  availableThemes: ThemeKey[];
  customization?: WhitelabelCustomization;
};

/**
 * List types for which column white/blacklists shall be allowed.
 *
 * If you want to extend the white/blacklisting of columns to a new table,
 * simply add a key here. This key shall then be used in the respective -config file
 * to filter the wanted/unwanted columns.
 *
 */
export enum WhitelabelTableType {
  EQUIPMENT_LIST = 'equipmentList',
  MAINTENANCE_LIST = 'maintenanceList',
  AUTOMATED_TRANSFER_LIST = 'automatedTransferList',
}

export enum WhitelabelFilterType {
  EQUIPMENT_LIST = 'equipmentList',
  MAINTENANCE_LIST = 'maintenanceList',
  AUTOMATED_TRANSFER_LIST = 'automatedTransferList',
}

export enum WhitelabelPropertyType {
  EQUIPMENT = 'equipment',
}

/**
 * Reference to the type of ColumnKeys that can be added to a column white/blacklisting.
 *
 * Add a new key-value pair once you want to extend the white/blacklisting of columns to a new table.
 */
type WhitelabelTableTypeColumnKeyMap = {
  [WhitelabelTableType.EQUIPMENT_LIST]: EquipmentColumnKeys[];
  [WhitelabelTableType.MAINTENANCE_LIST]: MaintenanceColumnKeys[];
  [WhitelabelTableType.AUTOMATED_TRANSFER_LIST]: AutomatedTransferColumnKeys[];
};

type WhitelabelFilterTypeMap = {
  [WhitelabelFilterType.EQUIPMENT_LIST]: string[]; // will be changed to matching type later
  [WhitelabelFilterType.MAINTENANCE_LIST]: string[]; // will be changed to matching type later
  [WhitelabelFilterType.AUTOMATED_TRANSFER_LIST]: string[]; // will be changed to matching type later
};

type WhitelabelPropertyTypeMap = {
  [WhitelabelPropertyType.EQUIPMENT]: BaseDataControl[]; // will be changed to matching type later
};

export enum WhitelabelVisibilityOption {
  BLACKLIST = 'blacklist',
  WHITELIST = 'whitelist',
}

export type WhitelabelCustomization = {
  visibility?: WhitelabelVisibilityCustomization;
  translationKeyOverrides?: Record<string, string>;
};

type WhitelabelVisibilityCustomization = {
  tableColumns?: WhitelabelTableColumnVisibilityDefinitions;
  filters?: WhitelabelFilterVisibilityDefinitions;
  properties?: WhitelabelPropertyVisibilityDefinitions;
};

// TABLE COLUMNS

type WhitelabelTableColumnDefinition<T extends WhitelabelTableType> = {
  type: WhitelabelVisibilityOption;
  columns: WhitelabelTableTypeColumnKeyMap[T];
};

type WhitelabelTableColumnVisibilityDefinitions = {
  [K in keyof WhitelabelTableTypeColumnKeyMap]?: WhitelabelTableColumnDefinition<K>;
};

// FILTERS

type WhitelabelFilterDefinition<T extends WhitelabelFilterType> = {
  type: WhitelabelVisibilityOption;
  filters: WhitelabelFilterTypeMap[T];
};

type WhitelabelFilterVisibilityDefinitions = {
  [K in keyof WhitelabelFilterTypeMap]?: WhitelabelFilterDefinition<K>;
};

// PROPERTIES

export type WhitelabelPropertyDefinition<T extends WhitelabelPropertyType> = {
  type: WhitelabelVisibilityOption;
  properties: WhitelabelPropertyTypeMap[T];
};

type WhitelabelPropertyVisibilityDefinitions = {
  [K in keyof WhitelabelPropertyTypeMap]?: WhitelabelPropertyDefinition<K>;
};
